import { ref, watch, nextTick, onUnmounted, Ref, unref } from 'vue'
import { Observer, StageSize } from '@/components/admin/documents/editor/DocumentPreview/types.ts'

interface Intersection {
  pageNumber: string | undefined
  intersectionRatio: number
}

export enum ScrollMode {
  ONE_PAGE = '1Page',
  TWO_PAGE = '2Page',
  CONTINUOUS = 'Continuous',
}

const deviation = 2

export const usePdfScroll = ({
  currentPage,
  setCurrentPage,
  setPageCount,
  scrollMode,
  scrollToCurrentPage,
  previewerDimensions,
  pageSize,
  setPageSize,
  setPageOneLoaded,
  getStageSize,
  getContainerEl = () => document.querySelector('#previewStage'),
  scrollToPageNumber,
  setScrollToPageNumber,
}: {
  currentPage: Ref<number>
  setCurrentPage: (page: number) => void
  setPageCount?: (pageCount: number) => void
  scrollMode: Ref<string>
  scrollToCurrentPage: (page: number) => boolean | void
  previewerDimensions: { width: number; height: number } | Ref<{ width: number; height: number }>
  getStageSize: (
    canvas: HTMLCanvasElement,
    previewerDimensions: { width: number; height: number },
  ) => StageSize | undefined
  pageSize: { width: number; height: number }
  setPageSize: (size: { width: number; height: number }) => void
  setPageOneLoaded?: (loaded: boolean) => void
  getContainerEl: () => Element | null | undefined
  scrollToPageNumber: Ref<number | undefined>
  setScrollToPageNumber: (page: number | undefined) => void
}) => {
  const visiblePages: Ref<Intersection[]> = ref([])
  const observer: Ref<Observer | null> = ref(null)
  const componentKey: Ref<number> = ref(1)
  const loading: Ref<boolean> = ref(true)
  const zoomChange: Ref<boolean> = ref(false)

  onUnmounted(() => {
    resetObserver()
  })

  const isContinuousMode = () => scrollMode.value === ScrollMode.CONTINUOUS

  const resetPDF = () => {
    componentKey.value += 1
    loading.value = true
  }

  const resetObserver = () => {
    if (observer.value) {
      observer.value.disconnect()
      observer.value = null
    }
    visiblePages.value = []
  }

  const setZoomChange = (value: boolean) => {
    zoomChange.value = value
  }

  const handleSetStageSize = (canvas: HTMLCanvasElement): StageSize | undefined => {
    const currentPreviewerDimensions = unref(previewerDimensions)
    const stageSize = getStageSize(canvas, currentPreviewerDimensions)
    if (
      stageSize &&
      (Math.abs(pageSize.width - stageSize.width) >= deviation ||
        Math.abs(pageSize.height - stageSize.height) >= deviation)
    ) {
      setPageSize({ width: stageSize.width, height: stageSize.height })
      return stageSize
    }
  }

  const createPdfPageObserver = () => {
    const containerEl = getContainerEl()
    if (!containerEl) {
      return
    }

    const options = {
      root: containerEl,
      threshold: [0, 0.5, 1],
    }
    const newObserver = new IntersectionObserver((pages) => {
      // Ignore stale callbacks from an observer that's since been disconnected/replaced
      if (observer.value === newObserver) {
        handlePageVisibility(pages)
      }
    }, options)
    observer.value = newObserver
    const pages = containerEl.querySelectorAll('.vue-pdf-embed__page')
    pages.forEach((page) => {
      newObserver.observe(page)
    })
  }

  // Scrolls to `page` and attaches the visibility observer, but only once enough page elements
  // exist in the DOM - used right after "loaded", before any "rendered" pass has happened yet.
  const setupScroll = (page: number, pageCount: number) => {
    const containerEl = getContainerEl()
    const pages = containerEl?.querySelectorAll('.vue-pdf-embed__page')
    if (!containerEl || !pages || pages.length < pageCount) {
      return
    }
    if (!scrollToCurrentPage(page)) {
      return
    }
    createPdfPageObserver()
  }

  const handleLoaded = async (numPages: number) => {
    setPageCount && setPageCount(numPages)
    if (currentPage.value > numPages) {
      setCurrentPage(1)
      await nextTick()
    }

    if (isContinuousMode()) {
      setupScroll(currentPage.value, numPages)
    }
  }

  const handleRendered = () => {
    const containerEl = getContainerEl()
    const canvas = containerEl?.querySelector('canvas')
    if (!canvas) {
      componentKey.value += 1
      return
    }

    const resizedStageSize = handleSetStageSize(canvas)
    resetObserver()

    if (resizedStageSize) {
      // The canvas size just changed - wait for the resulting re-render (with correct sizing)
      // before scrolling/observing
      return
    }

    if (isContinuousMode()) {
      createPdfPageObserver()
    }

    if (loading.value) {
      loading.value = false
      setPageOneLoaded && setPageOneLoaded(true)
      if (isContinuousMode() && currentPage.value > 1) {
        scrollToCurrentPage(currentPage.value)
      }
    }
    if (zoomChange.value) {
      zoomChange.value = false
      if (isContinuousMode()) {
        scrollToCurrentPage(currentPage.value)
      }
    }
  }

  const handleRenderingFailed = () => {
    componentKey.value += 1
  }

  const handlePageVisibility = (pages: IntersectionObserverEntry[]) => {
    if (zoomChange.value) {
      return
    }

    pages.forEach((page) => {
      const pageNumber = page.target.id.split('-').pop()
      const index = visiblePages.value.findIndex((item) => item.pageNumber === pageNumber)
      const pageIsAlreadyVisible = index > -1

      if (!page.isIntersecting && pageIsAlreadyVisible) {
        // A page has left the viewport
        visiblePages.value = visiblePages.value.toSpliced(index, 1)
        return
      }

      if (page.isIntersecting && !pageIsAlreadyVisible) {
        // A new page has entered the viewport
        visiblePages.value.push({
          pageNumber,
          intersectionRatio: page.intersectionRatio,
        })
        return
      }

      if (page.isIntersecting && pageIsAlreadyVisible) {
        // A page is still in the viewport
        visiblePages.value[index].intersectionRatio = page.intersectionRatio
      }
    })

    let mostVisiblePage: Intersection | undefined
    if (visiblePages.value.length === 1) {
      // There is one page visible in the viewport
      mostVisiblePage = visiblePages.value[0]
    } else if (visiblePages.value.length > 1) {
      // There are multiple pages visible in the viewport. Select the one that is the most visible
      mostVisiblePage = visiblePages.value.reduce((mostVisible, page) => {
        return page.intersectionRatio > mostVisible.intersectionRatio ? page : mostVisible
      })
    }

    if (mostVisiblePage?.pageNumber && mostVisiblePage.pageNumber !== String(currentPage.value)) {
      setCurrentPage(Number(mostVisiblePage.pageNumber))
    }
  }

  watch(
    () => scrollToPageNumber.value,
    (page) => {
      if (isContinuousMode() && page) {
        scrollToCurrentPage(currentPage.value)
        nextTick(() => setScrollToPageNumber(undefined))
      }
    },
    { deep: true },
  )

  return {
    handleLoaded,
    handleRendered,
    handleRenderingFailed,
    loading,
    componentKey,
    resetPDF,
    resetObserver,
    setZoomChange,
  }
}
